import React, { useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Column, DataTable, Text } from '@ydistri/ds';
import { BalancingField, BalancingGroupKey, BalancingGroupType } from './balancingLib';
import BalancingFieldTable from './BalancingFieldTable';
import { styled } from 'styled-components';
import { useGetBalancingConfigurationQuery } from './apiBalancingConfiguration';
import { CalculationConfigurationExperimentalType } from '@ydistri/api-sdk';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { computeRemSize } from '@ydistri/theme';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

const BalancingFieldsTableWrapper = styled(DataTable<BalancingField>)`
  .ant-table-tbody .ant-table-cell {
    vertical-align: top;
  }
`;

interface BalancingFieldsTableProps {
  groups: BalancingGroupType;
  groupId: BalancingGroupKey;
}

const BalancingFieldsTable: React.FC<BalancingFieldsTableProps> = ({ groups, groupId }) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: balancingData, isFetching } = useGetBalancingConfigurationQuery(
    templateOrCalculation.value,
  );

  const columns: ColumnsType<BalancingField> = useMemo(() => {
    return [
      {
        title: `Label`,
        dataIndex: ['label'],
        key: 'label',
        // width: computeRemSize(300),
        render: (value, row) => (
          <Column>
            <b>{value}</b>
            {row.description ? (
              <Text $size="small" $type="light">
                {row.description}
              </Text>
            ) : null}
          </Column>
        ),
      },
      {
        title: `Fields`,
        dataIndex: ['fieldKey'],
        key: 'fieldKey',
        width: computeRemSize(240),
        render: (value: CalculationConfigurationExperimentalType) => (
          <BalancingFieldTable fieldKey={value} />
        ),
      },
    ];
  }, []);

  const data = useMemo(() => {
    if (!balancingData) return [];

    const mainSettingType = groups[groupId]?.enableSetting;
    const hasMainSettingType = mainSettingType !== undefined;
    const mainSetting = mainSettingType
      ? balancingData.configuration[mainSettingType]?.values[0]
      : undefined;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know the type
    const fields = Object.keys(
      balancingData.configuration,
    ) as CalculationConfigurationExperimentalType[];

    let fieldsToHide: CalculationConfigurationExperimentalType[] = [];
    if (hasMainSettingType && mainSetting !== 1) {
      fieldsToHide = fields.filter(d => d !== mainSettingType).flat();
    } else {
      fieldsToHide = fields.map(d => balancingData.configuration[d]?.groupedWith ?? []).flat();
    }

    return (
      fields
        .filter(
          d => !fieldsToHide.includes(d) && balancingData.configuration[d]?.groupId === groupId,
        )
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- we know it's defined
        .map(d => balancingData.configuration[d]!)
    );
  }, [balancingData, groupId, groups]);

  if (isFetching) {
    return <LoadingIndicator />;
  }

  return (
    <BalancingFieldsTableWrapper
      id="balancing-admin"
      rowKey="key"
      dataSource={data}
      columns={columns}
      loading={false}
      minRowHeight="30px"
    />
  );
};

export default BalancingFieldsTable;
